import {
  ChangeEventHandler,
  ClipboardEventHandler,
  FocusEventHandler,
  KeyboardEventHandler,
  ReactNode,
  useLayoutEffect,
  useRef,
} from "react";
import { classNames } from "@/utils/classNames";
import { observer } from "mobx-react";
import { deferred, when } from "@/utils/functions";

import { useTranslation } from "@/stores/RootStoreContext";
import { StringFieldModel } from "@/common/commonForms";
import { isTMessage, TMessage } from "@/i18n/tt";

export type TextAreaProps = {
  label?: string | TMessage;
  labelAction?: ReactNode;
  placeholder?: string;
  name?: string;
  value: string;
  rows?: number;
  autofocus?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  error?: boolean;
  message?: string;
  maxLength?: number;
  enforceMaxLength?: boolean;
  expandable?: boolean;
  onBlur?: FocusEventHandler<HTMLTextAreaElement>;
  onChange?: ChangeEventHandler<HTMLTextAreaElement>;
  onKeyDown?: KeyboardEventHandler<HTMLTextAreaElement>;
  onPaste?: ClipboardEventHandler<HTMLTextAreaElement>;
};

export function TextArea({
  label,
  labelAction,
  placeholder,
  name,
  value,
  rows = 3,
  autofocus,
  disabled,
  readOnly,
  error,
  message,
  maxLength,
  enforceMaxLength = true,
  expandable,
  onBlur,
  onChange,
  onKeyDown,
  onPaste,
}: TextAreaProps) {
  const { t } = useTranslation();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => {
    if (!expandable || !textareaRef.current) {
      return;
    }

    const textarea = textareaRef.current;
    textarea.style.resize = "none";
    textarea.style.overflowY = "hidden";
    textarea.style.height = "auto";
    const borderHeight = textarea.offsetHeight - textarea.clientHeight;
    textarea.style.height = `${textarea.scrollHeight + borderHeight}px`;
  }, [expandable, value]);

  return (
    <label className={"grow"}>
      {label && (
        <div
          className={classNames(
            "mb-1",
            labelAction && ["flex", "justify-between", "items-center", "gap-2"],
          )}
        >
          {isTMessage(label) ? t(label) : label}
          {labelAction}
        </div>
      )}
      <textarea
        ref={textareaRef}
        className={classNames(
          "block",
          "border",
          "rounded-sm",
          "focus:ring-1",
          "w-full",
          "py-3",
          "px-4",
          error
            ? [
                "color-default",
                "bg-default",
                "border-error",
                "focus-within:border-error",
                "focus-within:ring-error",
                "hover:border-error",
                "hover:focus:border-error",
              ]
            : disabled
              ? ["subtle", "bg-gray-x-soft", "border-gray-soft", "hover:border-gray-soft"]
              : [
                  "color-default",
                  "bg-default",
                  "border-gray",
                  "focus:border-primary",
                  "focus:ring-primary",
                  "hover:border-gray-hard",
                  "hover:focus:border-primary",
                ],
        )}
        name={name}
        placeholder={placeholder}
        value={value}
        autoFocus={autofocus}
        readOnly={readOnly}
        disabled={disabled}
        onChange={onChange}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        rows={rows}
        maxLength={enforceMaxLength ? maxLength : undefined}
      />
      {maxLength ? (
        <div className={classNames("flex", "gap-2", "pt-1", "px-4", "text-sm")}>
          <div className={classNames("grow", error ? "color-error" : "subtle")}>{message}</div>
          <div className={"subtle"}>{`${value.length}/${maxLength}`}</div>
        </div>
      ) : (
        message && (
          <div className={classNames("pt-1", "pl-4", "text-sm", error ? "color-error" : "subtle")}>
            {message}
          </div>
        )
      )}
    </label>
  );
}

export type MTextAreaProps = {
  model: StringFieldModel;
} & Omit<TextAreaProps, "value">;

export const MTextArea = observer(function MTextInput({
  model,
  onChange,
  onBlur,
  onPaste,
  ...props
}: MTextAreaProps) {
  const { t } = useTranslation();
  return (
    <TextArea
      {...props}
      value={model.inputValue}
      onChange={onChange ?? model.setInputValue}
      onBlur={onBlur ?? model.commit}
      onPaste={onPaste ?? deferred(model.commit)}
      error={!model.isValid}
      message={when(model.error, t)}
    />
  );
});
